import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Visibility as ViewIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

// Mirrors BILL_CATEGORY_LIST in bills_routes.py (backend is the source of
// truth / validator) — this list is duplicated here for the dropdown, same
// pattern already used for BankImportInterface.jsx's category list, which
// bills_routes.py's own comments note is the "canonical" one ExpenseManagement.jsx
// has since drifted from. If this list and the backend list ever diverge,
// the backend will reject the mismatched value with a 400 — but keeping
// them in sync here avoids that happening in the first place.
const BILL_CATEGORIES = [
  'Accountancy & Legal', 'Bank Charges & Interest', 'Card Machine Fees',
  'Client Comfort', 'Communications', 'Electric', 'Furniture & Equipment',
  'Gas', 'Insurance', 'IT & Software', 'Leasing and Finance', 'Licences',
  'Loan Repayment', 'Maintenance & Repairs', 'Marketing & Advertising',
  'Office Supplies', 'Payroll', 'Personal', 'Postage and Courier',
  'Rates', 'Rent', 'Staff Welfare', 'Stock', 'Subscriptions', 'Sundry',
  'Taxation', 'Training', 'Utilities', 'Waste Disposal', 'Water'
];

const SupplierBillsTab = ({ supplierId, supplierName, onBillsChange }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openBillDialog, setOpenBillDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openQuickPurchase, setOpenQuickPurchase] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [categorySuggested, setCategorySuggested] = useState(false);

  // New Bill Form
  const [newBill, setNewBill] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    total_amount: '',
    vat_amount: '',
    category: '',
    notes: ''
  });

  // Payment Form
  const [payment, setPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference: '',
    notes: ''
  });

  // Quick Purchase Form
  const [quickPurchase, setQuickPurchase] = useState({
    purchase_date: new Date().toISOString().split('T')[0],
    amount: '',
    vat_amount: '',
    description: '',
    category: '',
    payment_method: 'cash'
  });

  useEffect(() => {
    if (supplierId) {
      fetchBills();
    }
  }, [supplierId]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `/api/bills/supplier/${supplierId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setBills(response.data.bills || []);
      setError('');
      
      // Update parent component with unpaid count
      if (onBillsChange) {
        const unpaidCount = (response.data.bills || []).filter(
          bill => bill.status === 'unpaid' || bill.status === 'partial'
        ).length;
        onBillsChange(unpaidCount);
      }
    } catch (err) {
      setError('Failed to load bills: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetches the supplier's most recently used category, purely as a
  // pre-filled SUGGESTION — never silently applied without the dialog
  // being open and visible, and always fully editable/clearable. Called
  // when the New Bill dialog opens rather than on mount, so it's re-fetched
  // fresh each time (in case the supplier's category history changed since
  // the last bill was created for them).
  const openNewBillDialog = async () => {
    setCategorySuggested(false);
    setNewBill(prev => ({ ...prev, category: '' }));
    setOpenBillDialog(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `/api/bills/supplier/${supplierId}/suggested-category`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const suggested = response.data?.suggested_category;
      if (suggested) {
        setNewBill(prev => ({ ...prev, category: suggested }));
        setCategorySuggested(true);
      }
    } catch (err) {
      // Non-fatal — the form works fine with no suggestion, just requires
      // a manual category pick. Not surfaced as an error banner since this
      // is a convenience feature, not a required step.
      console.warn('Could not fetch suggested category:', err.response?.data?.error || err.message);
    }
  };

  const handleCreateBill = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/bills',
        {
          supplier_id: supplierId,
          ...newBill,
          total_amount: parseFloat(newBill.total_amount),
          vat_amount: parseFloat(newBill.vat_amount || 0),
          category: newBill.category || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setOpenBillDialog(false);
      setNewBill({
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        total_amount: '',
        vat_amount: '',
        category: '',
        notes: ''
      });
      setCategorySuggested(false);
      fetchBills();
      setError('');
    } catch (err) {
      setError('Failed to create bill: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRecordPayment = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `/api/bills/${selectedBill.id}/pay`,
        {
          bill_id: selectedBill.id,
          amount: parseFloat(payment.amount),
          payment_date: payment.payment_date,
          payment_method: payment.payment_method,
          reference: payment.reference,
          notes: payment.notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setOpenPaymentDialog(false);
      setPayment({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer',
        reference: '',
        notes: ''
      });
      fetchBills();
      setError('');
    } catch (err) {
      setError('Failed to record payment: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleQuickPurchase = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/bills/quick-purchase',
        {
          supplier_id: supplierId,
          ...quickPurchase,
          amount: parseFloat(quickPurchase.amount),
          vat_amount: parseFloat(quickPurchase.vat_amount || 0),
          category: quickPurchase.category || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setOpenQuickPurchase(false);
      setQuickPurchase({
        purchase_date: new Date().toISOString().split('T')[0],
        amount: '',
        vat_amount: '',
        description: '',
        category: '',
        payment_method: 'cash'
      });
      fetchBills();
      setError('');
    } catch (err) {
      setError('Failed to record purchase: ' + (err.response?.data?.error || err.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partial':
        return 'warning';
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openNewBillDialog}
        >
          New Bill
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ReceiptIcon />}
          onClick={() => setOpenQuickPurchase(true)}
        >
          Quick Cash Purchase
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="All Bills" />
        <Tab label="Unpaid" />
        <Tab label="Paid" />
      </Tabs>

      {/* Bills Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Outstanding</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bills
              .filter(bill => {
                if (activeTab === 1) return bill.status === 'unpaid' || bill.status === 'partial';
                if (activeTab === 2) return bill.status === 'paid';
                return true;
              })
              .map((bill) => (
                <TableRow key={bill.id}>
                  <TableCell>{bill.invoice_number}</TableCell>
                  <TableCell>{new Date(bill.invoice_date).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell>{new Date(bill.due_date).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell align="right">{formatCurrency(bill.total_amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(bill.outstanding_amount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={bill.status.toUpperCase()}
                      color={getStatusColor(bill.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedBill(bill);
                        setPayment({ ...payment, amount: bill.outstanding_amount });
                        setOpenPaymentDialog(true);
                      }}
                      disabled={bill.status === 'paid'}
                      title="Record Payment"
                    >
                      <PaymentIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {bills.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No bills found for {supplierName}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Bill Dialog */}
      <Dialog open={openBillDialog} onClose={() => setOpenBillDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Bill - {supplierName}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Invoice Number"
                value={newBill.invoice_number}
                onChange={(e) => setNewBill({ ...newBill, invoice_number: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date"
                value={newBill.invoice_date}
                onChange={(e) => setNewBill({ ...newBill, invoice_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Due Date"
                value={newBill.due_date}
                onChange={(e) => setNewBill({ ...newBill, due_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Total Amount"
                value={newBill.total_amount}
                onChange={(e) => setNewBill({ ...newBill, total_amount: e.target.value })}
                InputProps={{ startAdornment: '£' }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="VAT Amount"
                value={newBill.vat_amount}
                onChange={(e) => setNewBill({ ...newBill, vat_amount: e.target.value })}
                InputProps={{ startAdornment: '£' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={newBill.category}
                onChange={(e) => {
                  setNewBill({ ...newBill, category: e.target.value });
                  setCategorySuggested(false);
                }}
                SelectProps={{ native: true }}
                helperText={categorySuggested ? `Suggested from this supplier's last bill — change if this one's different` : ' '}
              >
                <option value="">Select a category…</option>
                {BILL_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={newBill.notes}
                onChange={(e) => setNewBill({ ...newBill, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBillDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateBill} variant="contained" color="primary">
            Create Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Record Payment - {selectedBill?.invoice_number}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Outstanding: {selectedBill && formatCurrency(selectedBill.outstanding_amount)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Payment Amount"
                value={payment.amount}
                onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                InputProps={{ startAdornment: '£' }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Payment Date"
                value={payment.payment_date}
                onChange={(e) => setPayment({ ...payment, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={payment.payment_method}
                onChange={(e) => setPayment({ ...payment, payment_method: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference"
                value={payment.reference}
                onChange={(e) => setPayment({ ...payment, reference: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={payment.notes}
                onChange={(e) => setPayment({ ...payment, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained" color="primary">
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Purchase Dialog */}
      <Dialog open={openQuickPurchase} onClose={() => setOpenQuickPurchase(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Quick Cash Purchase - {supplierName}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={quickPurchase.description}
                onChange={(e) => setQuickPurchase({ ...quickPurchase, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount"
                value={quickPurchase.amount}
                onChange={(e) => setQuickPurchase({ ...quickPurchase, amount: e.target.value })}
                InputProps={{ startAdornment: '£' }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="VAT Amount"
                value={quickPurchase.vat_amount}
                onChange={(e) => setQuickPurchase({ ...quickPurchase, vat_amount: e.target.value })}
                InputProps={{ startAdornment: '£' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Purchase Date"
                value={quickPurchase.purchase_date}
                onChange={(e) => setQuickPurchase({ ...quickPurchase, purchase_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={quickPurchase.payment_method}
                onChange={(e) => setQuickPurchase({ ...quickPurchase, payment_method: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Category"
                value={quickPurchase.category}
                onChange={(e) => setQuickPurchase({ ...quickPurchase, category: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select a category…</option>
                {BILL_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQuickPurchase(false)}>Cancel</Button>
          <Button onClick={handleQuickPurchase} variant="contained" color="primary">
            Record Purchase
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierBillsTab;